import {
  attendantEntitySchema,
  companySchema,
  contestEntitySchema,
  dayEntitySchema,
  lectureEntitySchema,
  participantPassSchema,
  participantEntitySchema,
  participantScanLogEntitySchema,
  roomEntitySchema,
  speakerEntitySchema,
  tagEntitySchema,
  sessionEntitySchema,
} from "@/db/schema";
import z from "zod";
import { pl } from "zod/locales";

z.config(pl());

export const paginatedResponseSchema = z.object({
  total: z.number(),
  offset: z.number(),
});

export const speakerDtoSchema = speakerEntitySchema.pick({
  id: true,
  fullName: true,
});

export type SpeakerDto = z.infer<typeof speakerDtoSchema>;

export const speakersDtoSchema = paginatedResponseSchema.extend({
  items: z.array(speakerDtoSchema),
});

export const createSpeakerDtoSchema = speakerEntitySchema.pick({
  fullName: true,
});

export type SpeakerFormDto = z.infer<typeof createSpeakerDtoSchema>;

export const tagDtoSchema = tagEntitySchema.pick({
  id: true,
  name: true,
});

export type TagDto = z.infer<typeof tagDtoSchema>;

export const tagsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(tagDtoSchema),
});

export const createTagDtoSchema = tagEntitySchema.pick({
  name: true,
});

export type TagFormDto = z.infer<typeof createTagDtoSchema>;

export const roomDtoSchema = roomEntitySchema.pick({
  id: true,
  name: true,
  description: true,
});

export type RoomDto = z.infer<typeof roomDtoSchema>;

export const roomsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(roomDtoSchema),
});

export const createRoomDtoSchema = roomEntitySchema.pick({
  name: true,
  description: true,
});

export type RoomFormDto = z.infer<typeof createRoomDtoSchema>;

export const sessionDtoSchema = sessionEntitySchema
  .pick({
    id: true,
    title: true,
  })
  .extend({
    moderators: z.array(speakerDtoSchema),
    categories: z.array(tagDtoSchema),
  });

export type SessionDto = z.infer<typeof sessionDtoSchema>;

export const sessionsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(sessionDtoSchema),
});

export const createSessionDtoSchema = sessionEntitySchema
  .pick({
    title: true,
  })
  .extend({
    description: z.string().optional(),
    moderatorIds: z.array(z.number()).optional(),
    tagIds: z.array(z.number()).optional(),
  });

export type SessionFormDto = z.infer<typeof createSessionDtoSchema>;

export const lectureDtoSchema = lectureEntitySchema
  .pick({
    id: true,
    title: true,
    description: true,
    startTime: true,
    endTime: true,
    dayDate: true,
    order: true,
  })
  .extend({
    room: roomDtoSchema.nullable(),
    session: sessionDtoSchema.nullable(),
    speakers: z.array(speakerDtoSchema),
    categories: z.array(tagDtoSchema),
  });
export type LectureDto = z.infer<typeof lectureDtoSchema>;

export const lecturesDtoSchema = paginatedResponseSchema.extend({
  items: z.array(lectureDtoSchema),
});

export type LecturesDto = z.infer<typeof lecturesDtoSchema>;

export const createLectureDtoSchema = lectureEntitySchema
  .pick({
    title: true,
    startTime: true,
    endTime: true,
    dayDate: true,
    roomId: true,
  })
  .extend({
    description: z.string().nullish(),
    speakerIds: z.array(z.number()).optional(),
    tagIds: z.array(z.number()).optional(),
    sessionId: z.number().nullish(),
    session: sessionDtoSchema.nullish(),
    order: z.number().nullish(),
  });

export type LectureFormDto = z.infer<typeof createLectureDtoSchema>;

export const dayDtoSchema = z.object({
  id: dayEntitySchema.shape.id,
  date: dayEntitySchema.shape.date,
  lectures: z.array(lectureDtoSchema),
});

export type DayDto = z.infer<typeof dayDtoSchema>;

export const daysDtoSchema = paginatedResponseSchema.extend({
  items: z.array(dayDtoSchema),
});

export const createDayDtoSchema = dayEntitySchema.pick({ date: true });

export type DayFormDto = z.infer<typeof createDayDtoSchema>;

export const participantPassesDtoSchema = paginatedResponseSchema.extend({
  items: z.array(participantPassSchema),
});

export const createParticipantPassDtoSchema = participantPassSchema.pick({
  type: true,
  identifier: true,
  status: true,
  comments: true,
});

export type ParticipantPassDto = z.infer<typeof createParticipantPassDtoSchema>;

export const companyDtoSchema = companySchema.extend({
  categories: z.array(tagDtoSchema),
});

export type CompanyResponseDto = z.infer<typeof companyDtoSchema>;

export const companiesDtoSchema = paginatedResponseSchema.extend({
  items: z.array(companyDtoSchema),
});

export const createCompanyDtoSchema = companySchema
  .pick({
    title: true,
    standNumber: true,
    contactDetails: true,
    pin: true,
  })
  .extend({
    description: z.string().nullish(),
    linkUrl: z.string().nullish(),
    youngDoctorsSupport: z.boolean().nullish(),
    order: z.number().nullish(),
    tagIds: z.array(z.number()).optional(),
  });

export type CompanyDto = z.infer<typeof createCompanyDtoSchema>;

export const participantSchema = participantEntitySchema.extend({
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Participant = z.infer<typeof participantSchema>;

export const participantsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(participantSchema),
});

export const createParticipantDtoSchema = participantEntitySchema
  .pick({
    identifier: true,
  })
  .extend({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    speciality: z.string().nullish(),
    school: z.string().nullish(),
    city: z.string().nullish(),
    workplace: z.string().nullish(),
    gdprConsent: z.boolean().nullish(),
    marketingConsent: z.boolean().nullish(),
    youngDoctorConsent: z.boolean().nullish(),
  });

export type ParticipantDto = z.infer<typeof createParticipantDtoSchema>;

export const contestDtoSchema = contestEntitySchema.extend({
  company: companySchema.pick({ id: true, title: true }),
});

export type ContestDto = z.infer<typeof contestDtoSchema>;

export const contestsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(contestDtoSchema),
});

export const createContestDtoSchema = contestEntitySchema.pick({
  name: true,
  description: true,
  dueDate: true,
  linkUrl: true,
  companyId: true,
});

export type ContestFormDto = z.infer<typeof createContestDtoSchema>;

export const attendantDtoSchema = attendantEntitySchema.pick({
  id: true,
  fullName: true,
  pin: true,
});

export type AttendantDto = z.infer<typeof attendantDtoSchema>;

export const attendantsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(attendantDtoSchema),
});

export const createAttendantDtoSchema = attendantEntitySchema.pick({
  fullName: true,
  pin: true,
});

export type AttendantFormDto = z.infer<typeof createAttendantDtoSchema>;

export const createParticipantScanLogDtoSchema = participantScanLogEntitySchema
  .pick({
    identifier: true,
    firstName: true,
    lastName: true,
    email: true,
  })
  .extend({
    comment: z.string().nullish(),
    phone: z.string().nullish(),
    speciality: z.string().nullish(),
    school: z.string().nullish(),
    city: z.string().nullish(),
    workplace: z.string().nullish(),
    gdprConsent: z.boolean().nullish(),
    marketingConsent: z.boolean().nullish(),
    youngDoctorConsent: z.boolean().nullish(),
  });

export type ParticipantScanLogDto = z.infer<
  typeof createParticipantScanLogDtoSchema
>;

export const scanLogResponseDtoSchema = participantScanLogEntitySchema.extend({
  createdAt: z.string(),
  updatedAt: z.string(),
  addedBy: z.string().nullable(),
});
export type ScanLogResponseDto = z.infer<typeof scanLogResponseDtoSchema>;

export const scanLogsDtoSchema = paginatedResponseSchema.extend({
  items: z.array(scanLogResponseDtoSchema),
});

export const attendantLoginDtoSchema = z.object({
  pin: z.string(),
});

export const adminLoginDtoSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

export const participantLoginDtoSchema = z.object({
  identifier: z.string(),
});

export const companyLoginDtoSchema = z.object({
  pin: z.string(),
});
